// components/CardsSection.tsx
import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Snackbar,
  Alert,
  Box,
  Stack,
  CircularProgress,
  IconButton,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { createLinkToken, clearMessages } from "../../redux/bankSlice";
import PlaidLink from "../bank/PlaidLink";

type ViewState = "main" | "connecting";

const CardsSection = () => {
  const [currentView, setCurrentView] = useState<ViewState>("main");
  const dispatch = useAppDispatch();
  const { linkToken, accounts, loading, error, success } = useAppSelector(
    (state) => state.bank
  );

  const handleConnectBank = () => {
    dispatch(createLinkToken());
    setCurrentView("connecting");
  };

  const handleCancel = () => {
    setCurrentView("main");
    dispatch(clearMessages());
  };

  const handleSuccess = () => {
    setCurrentView("main");
  };

  const handleCloseSnackbar = () => {
    dispatch(clearMessages());
  };

  return (
    <Card sx={{ maxWidth: 500, margin: "auto", mt: 4 }}>
      <CardContent>
        {currentView === "main" ? (
          <>
            <Typography variant="h6" gutterBottom>
              Bank Accounts
            </Typography>

            {accounts.length > 0 ? (
              <List>
                {accounts.map((account) => (
                  <ListItem key={account.account_id}>
                    <ListItemIcon>
                      <Avatar>
                        <AccountBalanceIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={account.name}
                      secondary={`•••• ${account.mask} (${account.subtype})`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No bank accounts connected
              </Typography>
            )}

            <Box mt={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleConnectBank}
                disabled={loading}
              >
                Link Bank Account
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <IconButton onClick={handleCancel}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">Link your bank</Typography>
            </Stack>

            {linkToken ? (
              <PlaidLink token={linkToken} onSuccess={handleSuccess} />
            ) : (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            )}

            <Box mt={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                fullWidth
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}

        <Snackbar
          open={!!error || !!success}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={error ? "error" : "success"}
            sx={{ width: "100%" }}
          >
            {error || success}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default CardsSection;
